/** Sentinel for “All” in entity filter dropdowns (events, programs, opportunities, …). */
export const FILTER_SELECTION_ALL = "__all__";

export function isFilterSelectionAll(selectedId, allValue = FILTER_SELECTION_ALL) {
  return selectedId == null || selectedId === "" || String(selectedId) === String(allValue);
}

export function optionListContainsId(options, id) {
  if (id == null || id === "") return false;
  const idStr = String(id);
  return (options || []).some((o) => String(o?._id) === idStr);
}

/**
 * Whether a filter dropdown selection should stay, wait for options, or clear.
 *
 * Use when validating `selectedId` against a (possibly status-filtered) option list.
 * Waiting while `optionsReady` is false avoids racing URL drill-in / deep links
 * (clearing to All during load can leave wrong rows on screen from a stale fetch).
 *
 * @param {object} args
 * @param {string|null|undefined} args.selectedId
 * @param {Array<{_id?: *}>|null|undefined} args.optionList - options currently offered in the UI
 * @param {boolean} args.optionsReady - true after the master list has finished loading
 * @param {string|null|undefined} [args.preserveSelectedId] - URL drill-in id; keep even if hidden by status filter
 * @param {string} [args.allValue]
 * @returns {'keep'|'wait'|'clear'}
 */
export function getFilterSelectionValidityAction({
  selectedId,
  optionList,
  optionsReady,
  preserveSelectedId,
  allValue = FILTER_SELECTION_ALL,
}) {
  if (isFilterSelectionAll(selectedId, allValue)) return "keep";
  if (!optionsReady) return "wait";
  const pinned =
    preserveSelectedId != null && String(preserveSelectedId).trim() !== ""
      ? String(preserveSelectedId).trim()
      : "";
  if (pinned && pinned === String(selectedId)) return "keep";
  if (optionListContainsId(optionList, selectedId)) return "keep";
  return "clear";
}

/**
 * Client-side guard: when a specific entity is selected, never show other entities' rows
 * (covers stale “All” API responses racing a drill-in selection).
 */
export function filterRowsBySelectedForeignKey(
  rows,
  foreignKey,
  selectedId,
  allValue = FILTER_SELECTION_ALL
) {
  if (isFilterSelectionAll(selectedId, allValue)) return Array.isArray(rows) ? rows : [];
  const idStr = String(selectedId);
  return (Array.isArray(rows) ? rows : []).filter((row) => String(row?.[foreignKey]) === idStr);
}
